import React, { useRef, useEffect } from 'react';
import { Phone, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { gsap } from 'gsap';

const CTASection = () => {
  const button1Ref = useRef(null);
  const button2Ref = useRef(null);

  // Button hover animations
  useEffect(() => {
    const buttons = [button1Ref.current, button2Ref.current];
    
    const handlers = buttons.map(button => {
      if (!button) return null;
      
      const handleEnter = () => gsap.to(button, { scale: 1.05, duration: 0.3, ease: 'power2.out' });
      const handleLeave = () => gsap.to(button, { scale: 1, duration: 0.3, ease: 'power2.out' });
      const handleDown = () => gsap.to(button, { scale: 0.95, duration: 0.1 });
      const handleUp = () => gsap.to(button, { scale: 1.05, duration: 0.1 });

      button.addEventListener('mouseenter', handleEnter);
      button.addEventListener('mouseleave', handleLeave);
      button.addEventListener('mousedown', handleDown);
      button.addEventListener('mouseup', handleUp);

      return { button, handleEnter, handleLeave, handleDown, handleUp };
    });

    return () => {
      handlers.forEach(h => {
        if (h) {
          h.button.removeEventListener('mouseenter', h.handleEnter);
          h.button.removeEventListener('mouseleave', h.handleLeave);
          h.button.removeEventListener('mousedown', h.handleDown);
          h.button.removeEventListener('mouseup', h.handleUp);
        }
      });
    };
  }, []);

  return (
    <section id="cta" className="relative py-24 overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_green-funding-bros/artifacts/dbxz9ael_cta%20background.png')`,
        }}
      >
        <div className="absolute inset-0 bg-[#0A1628]/75"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          {/* Heading */}
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-2">
            READY TO
          </h2>
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-[#00A86B] mb-8">
            GROW?
          </h2>
          
          {/* Subtext */}
          <p className="text-white/90 text-lg md:text-xl mb-10 leading-relaxed">
            Stop waiting on banks. Get the equipment you need to land bigger jobs and grow your waste management business.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Button 
              ref={button1Ref}
              className="bg-[#00A86B] hover:bg-[#008f5b] text-white font-bold px-8 py-5 text-lg rounded-full flex items-center gap-2"
            >
              GET STARTED NOW
              <ArrowRight className="w-5 h-5" />
            </Button>
            <Button 
              ref={button2Ref}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#0A1628] font-bold px-8 py-5 text-lg rounded-full bg-transparent flex items-center gap-2"
            >
              <Phone className="w-5 h-5" />
              1-800-WASTEBRO
            </Button>
          </div>

          {/* Contact Info */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-white/80">
            <a href="tel:1-800-927-8327" className="flex items-center gap-2 hover:text-[#00A86B] transition-colors">
              <Phone className="w-4 h-4" />
              <span className="font-medium">1-800-WASTEBRO</span>
            </a>
            <a href="mailto:fund@wasteloanbros.com" className="flex items-center gap-2 hover:text-[#00A86B] transition-colors">
              <span className="font-medium">fund@wasteloanbros.com</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
